import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { BaseComponent } from '@macs-frontend/core';
import { DateFormatStore } from '@macs-frontend/core/store/date-format.store';
import { State } from '@macs-frontend/reducers';
import { Store } from '@ngrx/store';
import { CurrentCompanyService } from '../../../../core/services/current-company.service';
import { AppDateTime, toAppDateTime } from '@macs-frontend/core/utils/datetime';

export const invalidDatepickerInput = 'invalid datepicker input';

@Component({
  selector: 'md-datepicker',
  templateUrl: 'datepicker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
  ],
})
export class DatepickerComponent extends BaseComponent implements ControlValueAccessor {
  @Input() inputDisabled: boolean | null = false;
  @Input() defaultValue: AppDateTime | undefined = toAppDateTime(new Date());
  @Input() inputClasses = '';
  @Input() inputStyles: { [klass: string]: any } = {};
  @Input() format?: string;
  @Input() startView: 'month' | 'year' | 'multi-year' = 'month';
  @Input() mode: 'full' | 'year-only' | 'month-only' = 'full';
  @Input() disabled = false;
  @Output() dateChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() prefix?: string;
  @Output() pickerOpened: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() monthSelected = new EventEmitter<Date>();
  @Output() yearSelected = new EventEmitter<Date>();
  /**
   * Return null if user entered wrong date string
   */
  @Input() returnNullInCaseInvalidInput = true;

  // @ts-expect-error
  @ViewChild('picker') picker: MatDatepicker<Date>;
  // @ts-expect-error
  @ViewChild('input') input: ElementRef;

  size = 'default';

  constructor(
    public currentCompanyService: CurrentCompanyService,
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store<State>,
    readonly dateFormatStore: DateFormatStore,
  ) {
    super();
  }

  get model() {
    return this.modelValue || this.defaultValue;
  }

  set model(value) {
    this.writeValue(value);
    this.onChangeCallback(value);
  }

  private modelValue: any;

  onChangeCallback = (_: any) => {};

  writeValue(value: any) {
    if (value && !(value instanceof Date)) {
      // if value is not valid date MatDatepicker displays empty string
      // so if we need to keep invalid input string in datepicker input
      // we need to prevent value update
      return;
    }

    if (value && this.format) {
      (value as any).format = this.format;
    }

    this.modelValue = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (_: any) => void) {
    this.onChangeCallback = fn;
  }

  registerOnTouched() {}

  openPicker() {
    this.picker.open();
    this.pickerOpened.emit(true);
    this.cdr.markForCheck();
  }

  chosenYearHandler(event: Date) {
    if (this.mode === 'year-only') {
      this.model = event;
      this.picker.close();
    }
    this.yearSelected.emit(event);
  }

  chosenMonthHandler(event: Date) {
    if (this.mode === 'month-only') {
      this.model = event;
      this.picker.close();
    }
    this.monthSelected.emit(event);
  }

  onDateChange($event: MatDatepickerInputEvent<Date, unknown>) {
    if (
      !this.returnNullInCaseInvalidInput &&
      $event.value === null &&
      this.input.nativeElement.value !== ''
    ) {
      this.model = invalidDatepickerInput;
    } else {
      this.model = $event.value;
    }

    this.dateChange.next($event);
  }
}
